import { useState, useMemo } from 'react'
import useSWR from 'swr'
import dayjs from 'dayjs'
import {
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine,
  Legend,
  ComposedChart,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { CalendarIcon } from '@radix-ui/react-icons'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import { fmtK, fmtPct, fmtMoney } from '@/lib/format-utils'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useRecoveryStore } from '@/stores/recovery-store'
import Loader from '@/components/zthd/Loader'

const KEY_DAYS = [0, 3, 7, 14, 30, 60, 90, 120, 150]

// Custom label for reference lines
const CustomLabel = ({ viewBox, value, fill, position }) => {
  const { x, y, width } = viewBox || {}
  let textX = x
  let textY = y
  let textAnchor = 'start'

  if (position === 'insideTopRight') {
    textX = x + 5
    textY = 20
    textAnchor = 'start'
  } else if (position === 'insideRight') {
    textX = (width || 0) - 10
    textY = y - 5
    textAnchor = 'end'
  }

  return (
    <text
      x={textX}
      y={textY}
      fill={fill}
      fontSize={12}
      fontWeight="bold"
      textAnchor={textAnchor}
    >
      {value}
    </text>
  )
}

const ModelVsActual = ({ roasData }) => {
  const { pid, country } = useRecoveryStore()

  // Campaign start date - the day investment started
  const [campaignStartDate, setCampaignStartDate] = useState(
    dayjs().subtract(90, 'day').toDate(),
  )

  // Calculate end date based on model data length
  const campaignEndDate = useMemo(() => {
    if (!roasData?.length) return dayjs().toDate()
    const maxDays = roasData.length
    return dayjs(campaignStartDate)
      .add(maxDays - 1, 'day')
      .toDate()
  }, [campaignStartDate, roasData])

  // Query params for actual data
  const queryParams = useMemo(
    () => ({
      pid,
      country,
      startDate: dayjs(campaignStartDate).format('YYYYMMDD'),
      endDate: dayjs(campaignEndDate).format('YYYYMMDD'),
    }),
    [pid, country, campaignStartDate, campaignEndDate],
  )

  // Fetch actual costs and revenues
  const { data: costsData, isLoading: costsLoading } = useSWR([
    '/costs',
    queryParams,
  ])
  const { data: revenuesData, isLoading: revenuesLoading } = useSWR([
    '/revenues',
    queryParams,
  ])

  const isLoading = costsLoading || revenuesLoading

  // Build model cumulative rate map (day -> cumulative rate as decimal)
  const modelCumulativeRateMap = useMemo(() => {
    if (!roasData?.length) return {}
    const map = {}
    roasData.forEach((item) => {
      map[item.day] = item.roas / 100 // Convert percentage to decimal
    })
    return map
  }, [roasData])

  // Build model daily increment rate map (day -> daily rate as decimal)
  // daily_rate[N] = cumulative_rate[N] - cumulative_rate[N-1]
  const modelDailyRateMap = useMemo(() => {
    if (!roasData?.length) return {}
    const map = {}
    roasData.forEach((item, index) => {
      const currentRate = item.roas / 100
      const prevRate = index > 0 ? roasData[index - 1].roas / 100 : 0
      map[item.day] = currentRate - prevRate
    })
    return map
  }, [roasData])

  // Process and compare data
  const comparisonData = useMemo(() => {
    if (!costsData || !roasData?.length) return []

    // Get all dates from campaign start
    const dates = []
    let currentDate = dayjs(campaignStartDate)
    const endDate = dayjs(campaignEndDate)

    while (
      currentDate.isBefore(endDate) ||
      currentDate.isSame(endDate, 'day')
    ) {
      dates.push(currentDate.format('YYYYMMDD'))
      currentDate = currentDate.add(1, 'day')
    }

    // Collect daily costs
    const dailyCosts = dates.map((date) => costsData?.[date]?.total?.costs || 0)

    // Calculate predicted and actual revenue for each day
    const result = []
    let cumulativeCost = 0
    let cumulativePredictedRevenue = 0
    let cumulativeActualRevenue = 0

    dates.forEach((date, dayIndex) => {
      const dayCost = dailyCosts[dayIndex]
      cumulativeCost += dayCost

      // Calculate predicted DAILY revenue for today:
      // Today's revenue = sum of (each past day's cost × daily increment rate for days elapsed)
      // e.g., Dec 15's predicted revenue:
      //   - Dec 14's cost × daily_rate[1] (1 day elapsed)
      //   - Dec 13's cost × daily_rate[2] (2 days elapsed)
      //   - Dec 12's cost × daily_rate[3] (3 days elapsed)
      //   - ...
      let predictedDailyRevenue = 0
      for (let i = 0; i <= dayIndex; i++) {
        const costOnDayI = dailyCosts[i]
        const daysElapsed = dayIndex - i // How many days since Day i's investment
        const dailyRate = modelDailyRateMap[daysElapsed] || 0
        predictedDailyRevenue += costOnDayI * dailyRate
      }
      cumulativePredictedRevenue += predictedDailyRevenue

      // Actual daily revenue (already includes all historical costs' contribution)
      const actualDailyRevenue = revenuesData?.[date]?.total || 0
      cumulativeActualRevenue += actualDailyRevenue

      // Calculate ROAS
      const predictedRoas =
        cumulativeCost > 0
          ? (cumulativePredictedRevenue / cumulativeCost) * 100
          : 0
      const actualRoas =
        cumulativeCost > 0
          ? (cumulativeActualRevenue / cumulativeCost) * 100
          : 0

      result.push({
        day: dayIndex,
        dayLabel: `Day ${dayIndex}`,
        date,
        dateLabel: dayjs(date).format('MM/DD'),
        // Cost data
        dailyCost: dayCost,
        cumulativeCost,
        // Daily revenue
        predictedDailyRevenue,
        actualDailyRevenue,
        // Cumulative revenue
        predictedRevenue: cumulativePredictedRevenue,
        actualRevenue: cumulativeActualRevenue,
        revenueDiff: cumulativeActualRevenue - cumulativePredictedRevenue,
        // ROAS data
        predictedRoas,
        actualRoas,
        roasDiff: actualRoas - predictedRoas,
      })
    })

    return result
  }, [
    costsData,
    revenuesData,
    roasData,
    modelDailyRateMap,
    campaignStartDate,
    campaignEndDate,
  ])

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!comparisonData.length) return null

    const totalCost =
      comparisonData[comparisonData.length - 1]?.cumulativeCost || 0
    const totalPredictedRevenue =
      comparisonData[comparisonData.length - 1]?.predictedRevenue || 0
    const totalActualRevenue =
      comparisonData[comparisonData.length - 1]?.actualRevenue || 0

    const day30 = comparisonData.find((d) => d.day === 30)
    const day60 = comparisonData.find((d) => d.day === 60)
    const day90 = comparisonData.find((d) => d.day === 90)
    const latest = comparisonData[comparisonData.length - 1]

    return {
      totalCost,
      totalPredictedRevenue,
      totalActualRevenue,
      revenueDiff: totalActualRevenue - totalPredictedRevenue,
      day30,
      day60,
      day90,
      latest,
    }
  }, [comparisonData])

  // Calculate Y axis max for revenue chart
  const revenueYMax = useMemo(() => {
    if (!comparisonData.length) return 10000
    const maxValue = Math.max(
      ...comparisonData.map((d) =>
        Math.max(d.predictedRevenue || 0, d.actualRevenue || 0),
      ),
    )
    return Math.ceil(maxValue / 1000) * 1000
  }, [comparisonData])

  // Calculate Y axis max for ROAS chart
  const roasYMax = useMemo(() => {
    if (!comparisonData.length) return 200
    const maxValue = Math.max(
      ...comparisonData.map((d) =>
        Math.max(d.predictedRoas || 0, d.actualRoas || 0),
      ),
    )
    return Math.ceil(maxValue / 20) * 20
  }, [comparisonData])

  const handleDateSelect = (date) => {
    if (date) setCampaignStartDate(date)
  }

  if (!roasData || roasData.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center text-slate-500">
        暂无模型数据，请选择有效的参数。
      </div>
    )
  }

  return (
    <div className="space-y-6 py-4">
      {/* Campaign Start Date Picker */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">投放开始日期</CardTitle>
          <p className="text-sm text-slate-500">
            选择投放开始日期，系统将根据实际成本和模型回收率预测收入。
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-[240px] justify-start text-left font-normal',
                    !campaignStartDate && 'text-muted-foreground',
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {campaignStartDate ? (
                    format(campaignStartDate, 'yyyy-MM-dd')
                  ) : (
                    <span>选择开始日期</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="single"
                  selected={campaignStartDate}
                  onSelect={handleDateSelect}
                />
              </PopoverContent>
            </Popover>
            <span className="text-sm text-slate-500">
              → {format(campaignEndDate, 'yyyy-MM-dd')} (共{' '}
              {comparisonData.length} 天)
            </span>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <Loader>加载实际数据中...</Loader>
      ) : (
        <>
          {/* Summary Cards */}
          {summaryStats && (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-sm text-slate-500">累计成本</div>
                  <div className="text-2xl font-bold text-slate-700">
                    {fmtMoney(summaryStats.totalCost)}
                  </div>
                  <div className="mt-1 text-xs text-slate-400">
                    实际投放金额
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-sm text-slate-500">预测收入</div>
                  <div className="text-2xl font-bold text-blue-600">
                    {fmtMoney(summaryStats.totalPredictedRevenue)}
                  </div>
                  <div className="mt-1 text-xs text-slate-400">
                    基于模型计算
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-sm text-slate-500">实际收入</div>
                  <div className="text-2xl font-bold text-emerald-600">
                    {fmtMoney(summaryStats.totalActualRevenue)}
                  </div>
                  <div className="mt-1 text-xs text-slate-400">真实数据</div>
                </CardContent>
              </Card>

              <Card
                className={
                  summaryStats.revenueDiff >= 0
                    ? 'border-green-200 bg-green-50'
                    : 'border-red-200 bg-red-50'
                }
              >
                <CardContent className="pt-6">
                  <div className="text-sm text-slate-500">收入差额</div>
                  <div
                    className={`text-2xl font-bold ${
                      summaryStats.revenueDiff >= 0
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}
                  >
                    {summaryStats.revenueDiff >= 0 ? '+' : ''}
                    {fmtMoney(summaryStats.revenueDiff)}
                  </div>
                  <div className="mt-1 text-xs text-slate-400">实际 - 预测</div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Key Days ROAS Comparison */}
          {summaryStats && (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {summaryStats.day30 && (
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-sm text-slate-500">Day 30 ROAS</div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-bold text-blue-600">
                        {fmtPct(summaryStats.day30.predictedRoas)}
                      </span>
                      <span className="text-sm text-slate-400">vs</span>
                      <span className="text-lg font-bold text-emerald-600">
                        {fmtPct(summaryStats.day30.actualRoas)}
                      </span>
                    </div>
                    <div
                      className={`mt-1 text-xs ${
                        summaryStats.day30.roasDiff >= 0
                          ? 'text-green-600'
                          : 'text-red-600'
                      }`}
                    >
                      {summaryStats.day30.roasDiff >= 0 ? '+' : ''}
                      {fmtPct(summaryStats.day30.roasDiff)}
                    </div>
                  </CardContent>
                </Card>
              )}

              {summaryStats.day60 && (
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-sm text-slate-500">Day 60 ROAS</div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-bold text-blue-600">
                        {fmtPct(summaryStats.day60.predictedRoas)}
                      </span>
                      <span className="text-sm text-slate-400">vs</span>
                      <span className="text-lg font-bold text-emerald-600">
                        {fmtPct(summaryStats.day60.actualRoas)}
                      </span>
                    </div>
                    <div
                      className={`mt-1 text-xs ${
                        summaryStats.day60.roasDiff >= 0
                          ? 'text-green-600'
                          : 'text-red-600'
                      }`}
                    >
                      {summaryStats.day60.roasDiff >= 0 ? '+' : ''}
                      {fmtPct(summaryStats.day60.roasDiff)}
                    </div>
                  </CardContent>
                </Card>
              )}

              {summaryStats.day90 && (
                <Card className="border-purple-200 bg-purple-50">
                  <CardContent className="pt-6">
                    <div className="text-sm text-purple-600">Day 90 ROAS ★</div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-bold text-blue-600">
                        {fmtPct(summaryStats.day90.predictedRoas)}
                      </span>
                      <span className="text-sm text-slate-400">vs</span>
                      <span className="text-lg font-bold text-emerald-600">
                        {fmtPct(summaryStats.day90.actualRoas)}
                      </span>
                    </div>
                    <div
                      className={`mt-1 text-xs ${
                        summaryStats.day90.roasDiff >= 0
                          ? 'text-green-600'
                          : 'text-red-600'
                      }`}
                    >
                      {summaryStats.day90.roasDiff >= 0 ? '+' : ''}
                      {fmtPct(summaryStats.day90.roasDiff)}
                    </div>
                  </CardContent>
                </Card>
              )}

              {summaryStats.latest && (
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-sm text-slate-500">
                      Day {summaryStats.latest.day} ROAS
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-bold text-blue-600">
                        {fmtPct(summaryStats.latest.predictedRoas)}
                      </span>
                      <span className="text-sm text-slate-400">vs</span>
                      <span className="text-lg font-bold text-emerald-600">
                        {fmtPct(summaryStats.latest.actualRoas)}
                      </span>
                    </div>
                    <div
                      className={`mt-1 text-xs ${
                        summaryStats.latest.roasDiff >= 0
                          ? 'text-green-600'
                          : 'text-red-600'
                      }`}
                    >
                      {summaryStats.latest.roasDiff >= 0 ? '+' : ''}
                      {fmtPct(summaryStats.latest.roasDiff)}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Revenue Comparison Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">累计收入对比</CardTitle>
              <p className="text-sm text-slate-500">
                <span className="text-blue-600">●</span> 预测收入 (成本 ×
                模型回收率) | <span className="text-emerald-600">●</span>{' '}
                实际收入
              </p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={comparisonData}>
                  <XAxis
                    dataKey="day"
                    stroke="#64748b"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `D${value}`}
                  />
                  <YAxis
                    stroke="#64748b"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    domain={[0, revenueYMax]}
                    tickFormatter={(value) => fmtK(value)}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload
                        return (
                          <div className="rounded-lg border bg-background p-3 shadow-lg">
                            <div className="space-y-1">
                              <p className="font-semibold">
                                第{data.day}天 ({data.dateLabel})
                              </p>
                              <p className="text-sm text-slate-500">
                                当日成本: {fmtMoney(data.dailyCost)}
                              </p>
                              <p className="text-sm text-slate-500">
                                累计成本: {fmtMoney(data.cumulativeCost)}
                              </p>
                              <hr className="my-1" />
                              <p className="text-xs text-slate-400">
                                当日: 预测{fmtMoney(data.predictedDailyRevenue)}{' '}
                                vs 实际{fmtMoney(data.actualDailyRevenue)}
                              </p>
                              <p className="text-sm text-blue-600">
                                累计预测: {fmtMoney(data.predictedRevenue)}
                              </p>
                              <p className="text-sm text-emerald-600">
                                累计实际: {fmtMoney(data.actualRevenue)}
                              </p>
                              <p
                                className={`text-sm font-medium ${
                                  data.revenueDiff >= 0
                                    ? 'text-green-600'
                                    : 'text-red-600'
                                }`}
                              >
                                差额: {data.revenueDiff >= 0 ? '+' : ''}
                                {fmtMoney(data.revenueDiff)}
                              </p>
                            </div>
                          </div>
                        )
                      }
                      return null
                    }}
                  />
                  <Legend />

                  {/* D90 reference line */}
                  {comparisonData.length > 90 && (
                    <ReferenceLine
                      x={90}
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      label={
                        <CustomLabel
                          value="D90"
                          fill="#8b5cf6"
                          position="insideTopRight"
                        />
                      }
                    />
                  )}

                  <Line
                    type="monotone"
                    dataKey="predictedRevenue"
                    name="预测收入"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="actualRevenue"
                    name="实际收入"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* ROAS Comparison Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">ROAS 对比</CardTitle>
              <p className="text-sm text-slate-500">
                <span className="text-blue-600">●</span> 预测 ROAS |{' '}
                <span className="text-emerald-600">●</span> 实际 ROAS
              </p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={comparisonData}>
                  <XAxis
                    dataKey="day"
                    stroke="#64748b"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `D${value}`}
                  />
                  <YAxis
                    stroke="#64748b"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    domain={[0, roasYMax]}
                    tickFormatter={(value) => fmtPct(value)}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload
                        return (
                          <div className="rounded-lg border bg-background p-3 shadow-lg">
                            <div className="space-y-1">
                              <p className="font-semibold">
                                第{data.day}天 ({data.dateLabel})
                              </p>
                              <p className="text-sm text-blue-600">
                                预测 ROAS: {fmtPct(data.predictedRoas)}
                              </p>
                              <p className="text-sm text-emerald-600">
                                实际 ROAS: {fmtPct(data.actualRoas)}
                              </p>
                              <p
                                className={`text-sm font-medium ${
                                  data.roasDiff >= 0
                                    ? 'text-green-600'
                                    : 'text-red-600'
                                }`}
                              >
                                差异: {data.roasDiff >= 0 ? '+' : ''}
                                {fmtPct(data.roasDiff)}
                              </p>
                            </div>
                          </div>
                        )
                      }
                      return null
                    }}
                  />
                  <Legend />

                  {/* D90 reference line */}
                  {comparisonData.length > 90 && (
                    <ReferenceLine
                      x={90}
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      label={
                        <CustomLabel
                          value="D90 目标"
                          fill="#8b5cf6"
                          position="insideTopRight"
                        />
                      }
                    />
                  )}

                  {/* 100% breakeven line */}
                  <ReferenceLine
                    y={100}
                    stroke="#ef4444"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    label={
                      <CustomLabel
                        value="100% 回本线"
                        fill="#ef4444"
                        position="insideRight"
                      />
                    }
                  />

                  <Line
                    type="monotone"
                    dataKey="predictedRoas"
                    name="预测 ROAS"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="actualRoas"
                    name="实际 ROAS"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Comparison Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">每日对比明细</CardTitle>
              <p className="text-sm text-slate-500">
                预测当日收入 = Σ(历史每天成本 × 对应天数的增量回收率)
              </p>
            </CardHeader>
            <CardContent>
              <div className="max-h-[500px] overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background">
                    <TableRow>
                      <TableHead>天数</TableHead>
                      <TableHead>日期</TableHead>
                      <TableHead className="text-right">当日成本</TableHead>
                      <TableHead className="text-right">累计成本</TableHead>
                      <TableHead className="text-right text-blue-600">
                        预测收入(当日)
                      </TableHead>
                      <TableHead className="text-right text-emerald-600">
                        实际收入(当日)
                      </TableHead>
                      <TableHead className="text-right text-blue-600">
                        预测收入(累计)
                      </TableHead>
                      <TableHead className="text-right text-emerald-600">
                        实际收入(累计)
                      </TableHead>
                      <TableHead className="text-right text-blue-600">
                        预测ROAS
                      </TableHead>
                      <TableHead className="text-right text-emerald-600">
                        实际ROAS
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {comparisonData.map((item) => (
                      <TableRow
                        key={item.day}
                        className={
                          item.day === 90
                            ? 'bg-purple-100'
                            : KEY_DAYS.includes(item.day)
                            ? 'bg-slate-50'
                            : ''
                        }
                      >
                        <TableCell
                          className={`font-medium ${
                            item.day === 90
                              ? 'font-bold text-purple-700'
                              : KEY_DAYS.includes(item.day)
                              ? 'font-bold'
                              : ''
                          }`}
                        >
                          {item.dayLabel}
                          {item.day === 90 && (
                            <span className="ml-2 text-xs text-purple-500">
                              ★
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {item.dateLabel}
                        </TableCell>
                        <TableCell className="text-right text-slate-500">
                          {fmtMoney(item.dailyCost)}
                        </TableCell>
                        <TableCell className="text-right">
                          {fmtMoney(item.cumulativeCost)}
                        </TableCell>
                        <TableCell className="text-right text-blue-400">
                          {fmtMoney(item.predictedDailyRevenue)}
                        </TableCell>
                        <TableCell className="text-right text-emerald-400">
                          {fmtMoney(item.actualDailyRevenue)}
                        </TableCell>
                        <TableCell className="text-right text-blue-600 font-medium">
                          {fmtMoney(item.predictedRevenue)}
                        </TableCell>
                        <TableCell className="text-right text-emerald-600 font-medium">
                          {fmtMoney(item.actualRevenue)}
                        </TableCell>
                        <TableCell className="text-right text-blue-600">
                          {fmtPct(item.predictedRoas)}
                        </TableCell>
                        <TableCell className="text-right text-emerald-600">
                          {fmtPct(item.actualRoas)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}

export default ModelVsActual
